/**
 * CustomerDetailPage.js - 客户详情页（独立页面）
 * 展示客户信息、订单统计、订单历史，支持发送邮件
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Descriptions,
  Tag,
  Button,
  Table,
  Space,
  message,
  Spin,
  Typography,
  Modal,
  Input,
  Form,
  Select,
  Switch,
  Row,
  Col,
  Popconfirm,
} from 'antd';
import {
  ArrowLeftOutlined,
  MailOutlined,
  EditOutlined,
  StopOutlined,
  CheckCircleOutlined,
  SaveOutlined,
  KeyOutlined,
} from '@ant-design/icons';
import { adminUserApi, adminCustomerTagApi, adminOrderApi } from '../../api/index';

const { Title, Text } = Typography;
const { TextArea } = Input;

// 订单状态配置
const statusConfig = {
  pending: { color: 'orange', label: '待支付' },
  paid: { color: 'green', label: '已支付' },
  shipped: { color: 'blue', label: '已发货' },
  completed: { color: 'purple', label: '已完成' },
  cancelled: { color: 'red', label: '已取消' },
};

export default function CustomerDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [customer, setCustomer] = useState(null);
  const [stats, setStats] = useState({ orderCount: 0, totalSpent: 0 });
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ordersLoading, setOrdersLoading] = useState(false);

  // 编辑模式
  const [editing, setEditing] = useState(false);
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [tags, setTags] = useState([]);

  // 发送邮件弹窗
  const [emailModalVisible, setEmailModalVisible] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailContent, setEmailContent] = useState('');
  const [emailSending, setEmailSending] = useState(false);

  useEffect(() => {
    fetchCustomer();
    fetchTags();
  }, [id]);

  const fetchCustomer = async () => {
    setLoading(true);
    try {
      const user = await adminUserApi.getById(id);
      setCustomer(user);

      // 获取统计
      const statsData = await adminUserApi.getStats(id);
      setStats(statsData);

      // 获取订单列表
      setOrdersLoading(true);
      const ordersData = await adminUserApi.getOrders(id);
      setOrders(ordersData || []);
    } catch (err) {
      message.error('加载客户信息失败');
      navigate('/admin/customers');
    } finally {
      setLoading(false);
      setOrdersLoading(false);
    }
  };

  const fetchTags = async () => {
    try {
      const data = await adminCustomerTagApi.list();
      setTags(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('加载标签失败:', err);
    }
  };

  // 开始编辑
  const handleStartEdit = () => {
    let userTags = [];
    if (customer.tags) {
      userTags = typeof customer.tags === 'string' ? customer.tags.split(',').map(t => t.trim()).filter(Boolean) : customer.tags;
    }
    form.setFieldsValue({
      firstName: customer.firstName,
      lastName: customer.lastName,
      phone: customer.phone,
      tags: userTags,
      note: customer.note,
      enabled: customer.enabled !== false,
      marketingEmailSubscribed: customer.marketingEmailSubscribed !== false,
    });
    setEditing(true);
  };

  // 保存编辑
  const handleSaveEdit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const payload = {
        ...values,
        tags: values.tags && Array.isArray(values.tags) ? values.tags.join(',') : '',
      };
      await adminUserApi.update(customer.id, payload);
      message.success('保存成功');
      setEditing(false);
      fetchCustomer();
    } catch (err) {
      if (err?.errorFields) return;
      message.error('保存失败：' + (err.message || '未知错误'));
    } finally {
      setSaving(false);
    }
  };

  // 重置密码为 123456
  const handleResetPassword = async () => {
    try {
      await adminUserApi.resetPassword(customer.id);
      message.success('密码已重置为 123456，请告知用户首次登录后修改');
    } catch (err) {
      message.error('重置失败：' + (err?.response?.data?.message || err.message || '未知错误'));
    }
  };

  // 切换启用/禁用
  const handleToggleEnabled = async () => {
    try {
      const newEnabled = !customer.enabled;
      await adminUserApi.update(customer.id, { enabled: newEnabled });
      message.success(newEnabled ? '用户已启用' : '用户已禁用');
      fetchCustomer();
    } catch (err) {
      message.error('操作失败');
    }
  };

  // 发送邮件
  const handleSendEmail = async () => {
    if (!emailSubject || emailSubject.trim() === '') {
      message.error('请输入邮件主题');
      return;
    }
    if (!emailContent || emailContent.trim() === '') {
      message.error('请输入邮件内容');
      return;
    }

    setEmailSending(true);
    try {
      // 发送到客户的邮箱
      await adminOrderApi.sendEmail(null, {
        to: customer.email,
        subject: emailSubject,
        content: emailContent,
      });
      message.success('邮件已发送');
      setEmailModalVisible(false);
      setEmailSubject('');
      setEmailContent('');
    } catch (err) {
      message.error(err?.response?.data?.message || '发送失败');
    } finally {
      setEmailSending(false);
    }
  };

  // 订单表格列
  const orderColumns = [
    {
      title: '订单号',
      dataIndex: 'orderNo',
      key: 'orderNo',
      render: (orderNo, record) => (
        <Button type="link" onClick={() => navigate(`/admin/orders/${record.id}`)} style={{ padding: 0 }}>
          {orderNo}
        </Button>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const cfg = statusConfig[status] || { color: 'default', label: status };
        return <Tag color={cfg.color}>{cfg.label}</Tag>;
      },
    },
    {
      title: '金额',
      dataIndex: 'total',
      key: 'total',
      render: (total) => `$${total?.toFixed(2) || '0.00'}`,
    },
    {
      title: '下单时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (val) => val ? new Date(val).toLocaleString('zh-CN', { hour12: false }) : '-',
    },
  ];

  if (loading) {
    return <div style={{ padding: 24, textAlign: 'center' }}><Spin size="large" /></div>;
  }

  if (!customer) {
    return <div style={{ padding: 24 }}>客户不存在</div>;
  }

  const fullName = [customer.lastName, customer.firstName].filter(Boolean).join(' ') || '未设置姓名';

  return (
    <div>
      {/* 顶部操作栏 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/admin/customers')}>
            返回列表
          </Button>
          <Title level={4} style={{ margin: 0 }}>{fullName}</Title>
          <Tag color={customer.role === 'admin' ? 'blue' : 'default'}>
            {customer.role === 'admin' ? '管理员' : '客户'}
          </Tag>
          <Tag color={customer.enabled !== false ? 'green' : 'red'}>
            {customer.enabled !== false ? '正常' : '已禁用'}
          </Tag>
        </Space>
        <Space>
          {!editing && (
            <>
              <Button
                icon={<MailOutlined />}
                onClick={() => setEmailModalVisible(true)}
              >
                发送邮件
              </Button>
              <Button icon={<EditOutlined />} onClick={handleStartEdit}>
                编辑
              </Button>
              <Popconfirm
                title="确认将该用户密码重置为 123456？"
                description="请告知用户首次登录后修改密码。"
                onConfirm={handleResetPassword}
                okText="确认重置"
                cancelText="取消"
              >
                <Button icon={<KeyOutlined />}>
                  重置密码
                </Button>
              </Popconfirm>
              <Popconfirm
                title={customer.enabled !== false ? '确认禁用该用户？' : '确认启用该用户？'}
                onConfirm={handleToggleEnabled}
                okText="确认"
                cancelText="取消"
              >
                <Button
                  danger={customer.enabled !== false}
                  icon={customer.enabled !== false ? <StopOutlined /> : <CheckCircleOutlined />}
                >
                  {customer.enabled !== false ? '禁用' : '启用'}
                </Button>
              </Popconfirm>
            </>
          )}
          {editing && (
            <>
              <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSaveEdit}>
                保存
              </Button>
              <Button onClick={() => setEditing(false)}>取消</Button>
            </>
          )}
        </Space>
      </div>

      {/* 基本信息 */}
      <Card style={{ marginBottom: 16 }}>
        {!editing ? (
          <Descriptions column={2}>
            <Descriptions.Item label="邮箱">{customer.email}</Descriptions.Item>
            <Descriptions.Item label="手机号">{customer.phone || '-'}</Descriptions.Item>
            <Descriptions.Item label="姓名">{fullName}</Descriptions.Item>
            <Descriptions.Item label="标签">
              {customer.tags ? customer.tags.split(',').map(t => (
                <Tag key={t}>{t.trim()}</Tag>
              )) : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="备注">{customer.note || '-'}</Descriptions.Item>
            <Descriptions.Item label="订阅营销邮件">
              {customer.marketingEmailSubscribed !== false ? '是' : '否'}
            </Descriptions.Item>
            <Descriptions.Item label="注册时间">
              {customer.createdAt ? new Date(customer.createdAt).toLocaleString('zh-CN', { hour12: false }) : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="最后登录">
              {customer.lastLoginAt ? new Date(customer.lastLoginAt).toLocaleString('zh-CN', { hour12: false }) : '尚未登录'}
            </Descriptions.Item>
          </Descriptions>
        ) : (
          <Form form={form} layout="vertical">
            <Row gutter={12}>
              <Col span={12}>
                <Form.Item name="lastName" label="姓（Last Name）">
                  <Input placeholder="姓" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="firstName" label="名（First Name）">
                  <Input placeholder="名" />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item name="phone" label="手机号">
              <Input placeholder="+1 234 567 8900" />
            </Form.Item>
            <Form.Item name="tags" label="标签">
              <Select mode="multiple" placeholder="选择标签" allowClear>
                {tags.map((tag) => (
                  <Select.Option key={tag.id} value={tag.name}>
                    {tag.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="note" label="备注">
              <TextArea rows={3} placeholder="内部备注" />
            </Form.Item>
            <Form.Item name="enabled" label="账户状态" valuePropName="checked">
              <Switch checkedChildren="正常" unCheckedChildren="已禁用" />
            </Form.Item>
            <Form.Item name="marketingEmailSubscribed" label="订阅营销邮件" valuePropName="checked">
              <Switch checkedChildren="订阅" unCheckedChildren="退订" />
            </Form.Item>
          </Form>
        )}
      </Card>

      {/* 订单统计 */}
      <Card style={{ marginBottom: 16 }}>
        <Descriptions column={4}>
          <Descriptions.Item label="订单数">
            <Text strong style={{ fontSize: 18, color: '#1677ff' }}>{stats.orderCount || 0}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="消费总额">
            <Text strong style={{ fontSize: 18, color: '#52c41a' }}>${stats.totalSpent?.toFixed(2) || '0.00'}</Text>
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* 订单历史 */}
      <Card title="订单历史">
        <Table
          dataSource={orders}
          columns={orderColumns}
          rowKey="id"
          loading={ordersLoading}
          pagination={{ pageSize: 10 }}
          locale={{ emptyText: '暂无订单' }}
          onRow={(record) => ({
            onClick: () => navigate(`/admin/orders/${record.id}`),
            style: { cursor: 'pointer' },
          })}
        />
      </Card>

      {/* 发送邮件弹窗 */}
      <Modal
        title={<Space><MailOutlined /> 发送邮件给客户</Space>}
        open={emailModalVisible}
        onCancel={() => setEmailModalVisible(false)}
        onOk={handleSendEmail}
        okText="发送"
        cancelText="取消"
        confirmLoading={emailSending}
      >
        <div style={{ marginBottom: 12 }}>
          <Text type="secondary">收件人：{customer.email}</Text>
        </div>
        <Input
          placeholder="邮件主题"
          value={emailSubject}
          onChange={(e) => setEmailSubject(e.target.value)}
          style={{ marginBottom: 12 }}
        />
        <TextArea
          placeholder="邮件内容"
          value={emailContent}
          onChange={(e) => setEmailContent(e.target.value)}
          rows={6}
        />
      </Modal>
    </div>
  );
}